/**
 * Servicio de Reportes
 * Maneja generación y consulta de reportes del sistema
 */
import api from './api';

/**
 * Obtener reporte diario de cheques
 * @param {string} date - Fecha del reporte (ISO format)
 * @returns {Promise<Object>} Datos del reporte diario
 */
export const getDailyReport = async (date) => {
  try {
    const response = await api.get('/reportes/diario', {
      params: { fecha: date },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte diario:', error);
    throw error;
  }
};

/**
 * Obtener reporte semanal de cheques
 * @param {string} startDate - Fecha inicio (ISO format)
 * @param {string} endDate - Fecha fin (ISO format)
 * @returns {Promise<Object>} Datos del reporte semanal
 */
export const getWeeklyReport = async (startDate, endDate) => {
  try {
    const response = await api.get('/reportes/semanal', {
      params: { fecha_inicio: startDate, fecha_fin: endDate },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte semanal:', error);
    throw error;
  }
};

/**
 * Obtener reporte mensual de cheques
 * @param {number} month - Mes (1-12)
 * @param {number} year - Año
 * @returns {Promise<Object>} Datos del reporte mensual
 */
export const getMonthlyReport = async (month, year) => {
  try {
    const response = await api.get('/reportes/mensual', {
      params: { mes: month, anio: year },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte mensual:', error);
    throw error;
  }
};

/**
 * Obtener reporte de cheques pendientes
 * @param {Object} filters - Filtros opcionales (clienteId, bancoId)
 * @returns {Promise<Object>} Datos del reporte de pendientes
 */
export const getPendingReport = async (filters = {}) => {
  try {
    const response = await api.get('/reportes/pendientes', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de pendientes:', error);
    throw error;
  }
};

/**
 * Obtener calendario de cheques posfechados
 * @param {number} month - Mes (1-12)
 * @param {number} year - Año
 * @returns {Promise<Object>} Calendario de cheques posfechados
 */
export const getPostDatedCalendar = async (month, year) => {
  try {
    const response = await api.get('/reportes/calendario-posfechados', {
      params: { mes: month, anio: year },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener calendario de posfechados:', error);
    throw error;
  }
};

/**
 * Obtener reporte de cheques por cliente
 * @param {string} clienteId - ID del cliente
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Reporte del cliente
 */
export const getClienteReport = async (clienteId, filters = {}) => {
  try {
    const response = await api.get(`/reportes/cliente/${clienteId}`, {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de cliente:', error);
    throw error;
  }
};

/**
 * Obtener reporte de cheques por banco
 * @param {string} bancoId - ID del banco
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Reporte del banco
 */
export const getBancoReport = async (bancoId, filters = {}) => {
  try {
    const response = await api.get(`/reportes/banco/${bancoId}`, {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de banco:', error);
    throw error;
  }
};

/**
 * Obtener reporte de novedades
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta, tipo, estado)
 * @returns {Promise<Object>} Reporte de novedades
 */
export const getNovedadesReport = async (filters = {}) => {
  try {
    const response = await api.get('/reportes/novedades', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de novedades:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas generales del sistema
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Estadísticas generales
 */
export const getGeneralStats = async (filters = {}) => {
  try {
    const response = await api.get('/reportes/estadisticas', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas generales:', error);
    throw error;
  }
};

/**
 * Exportar reporte a PDF
 * @param {string} tipo - Tipo de reporte (diario, semanal, mensual, etc.)
 * @param {Object} params - Parámetros del reporte
 * @returns {Promise<Blob>} PDF del reporte
 */
export const exportReportToPDF = async (tipo, params = {}) => {
  try {
    const response = await api.get(`/reportes/${tipo}/export/pdf`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al exportar reporte a PDF:', error);
    throw error;
  }
};

/**
 * Exportar reporte a Excel
 * @param {string} tipo - Tipo de reporte (diario, semanal, mensual, etc.)
 * @param {Object} params - Parámetros del reporte
 * @returns {Promise<Blob>} Excel del reporte
 */
export const exportReportToExcel = async (tipo, params = {}) => {
  try {
    const response = await api.get(`/reportes/${tipo}/export/excel`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al exportar reporte a Excel:', error);
    throw error;
  }
};

/**
 * Descargar reporte en formato especificado
 * @param {Blob} blob - Blob del archivo
 * @param {string} filename - Nombre del archivo
 */
export const downloadReport = (blob, filename) => {
  try {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar reporte:', error);
    throw error;
  }
};

/**
 * Obtener reporte de cheques por cliente / dueño de cuenta
 * @param {Object} filters - Filtros (estado, fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Reporte agrupado por cliente
 */
export const getClientesChecksReport = async (filters = {}) => {
  try {
    const response = await api.get('/reportes/clientes', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de clientes:', error);
    throw error;
  }
};

/**
 * Obtener reporte de depósitos
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta, cuentaId)
 * @returns {Promise<Object>} Reporte de depósitos
 */
export const getDepositosReport = async (filters = {}) => {
  try {
    const response = await api.get('/reportes/depositos', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de depósitos:', error);
    throw error;
  }
};

export default {
  getDailyReport,
  getWeeklyReport,
  getMonthlyReport,
  getPendingReport,
  getPostDatedCalendar,
  getClienteReport,
  getBancoReport,
  getNovedadesReport,
  getGeneralStats,
  exportReportToPDF,
  exportReportToExcel,
  downloadReport,
  getDepositosReport,
  getClientesChecksReport,
};
